/**
 * StockImportDialog - Importação em lote de rastreadores (IMEI) ou chips (ICCID)
 *
 * Uma linha por item, colunas separadas por ";" (ou tab, ao colar de planilha).
 * Rastreador: IMEI;ESN — Chip: ICCID;Linha;IMSI.
 * Valores padrão (modelo/operadora/situação) valem para todas as linhas.
 * O banco grava as válidas e devolve o relatório das que falharam.
 *
 * RELEASE: 2026-10-08 v1.2.0 - Rastreador IMEI;ESN; Modelo/Plano com SearchableSelect.
 * RELEASE: 2026-10-08 v1.1.0 - Chip: campo Operadora substituído por seletor de Plano de dados
 *          (com atalho "+" para criar plano). Plano validado no servidor pelo cadastro de chip.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 3 do Estoque).
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InlineNotice } from '@/components/ui/inline-notice';
import { manualStatusesFor, useStockItemMutations, type ImportReport } from '@/hooks/useStockItems';
import { dataPlanCarrierLabel, useStockDataPlans, useTrackerModels } from '@/hooks/useStockRegistries';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { StockDataPlanFormDialog } from '@/components/stock/StockDataPlanFormDialog';
import { usePermissions } from '@/hooks/usePermissions';

const NONE = '__none__';
const MAX_LINES = 2000;

export function StockImportDialog({ kind, open, onOpenChange }: { kind: 'tracker' | 'chip'; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [text, setText] = useState('');
  const [status, setStatus] = useState('active');
  const [modelId, setModelId] = useState(NONE);
  // Plano de dados aplicado a todos os chips importados
  const [dataPlanId, setDataPlanId] = useState(NONE);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  const { importItems } = useStockItemMutations();
  const { canCreate } = usePermissions();
  const { data: models } = useTrackerModels({ search: '', status: 'active', page: 1, pageSize: 100 });
  const { data: plans } = useStockDataPlans({ search: '', status: 'active', page: 1, pageSize: 100 }, open && kind === 'chip');

  useEffect(() => {
    if (open) {
      setText('');
      setReport(null);
      setStatus('active');
      setModelId(NONE);
      setDataPlanId(NONE);
    }
  }, [open]);

  // Converte o texto colado em linhas (só dígitos nos campos numéricos)
  const rows = useMemo(() => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
    const cols = l.split(/[;\t]/).map((c) => c.trim());
    return kind === 'tracker'
      ? { imei: cols[0]?.replace(/\D/g, '') ?? '', esn: (cols[1] ?? '').replace(/[^A-Za-z0-9]/g, '').slice(0, 30) }
      : { iccid: cols[0]?.replace(/\D/g, '') ?? '', line_number: (cols[1] ?? '').replace(/\D/g, ''), imsi: (cols[2] ?? '').replace(/\D/g, '') };
  }), [text, kind]);

  const run = () => {
    const defaults: Record<string, unknown> = { status };
    if (kind === 'tracker' && modelId !== NONE) defaults.tracker_model_id = modelId;
    if (kind === 'chip' && dataPlanId !== NONE) defaults.data_plan_id = dataPlanId;
    importItems.mutate({ kind, rows, defaults }, { onSuccess: (r) => setReport(r) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>{kind === 'tracker' ? 'Importar rastreadores' : 'Importar chips'}</DialogTitle></DialogHeader>
        {report ? (
          <div className="space-y-3">
            <InlineNotice variant={report.errors.length ? 'warning' : 'success'}>
              {report.imported} de {report.total} {kind === 'tracker' ? 'rastreadores' : 'chips'} importados.
              {report.errors.length > 0 && ` ${report.errors.length} linha(s) com erro.`}
            </InlineNotice>
            {report.errors.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-md border text-sm">
                {report.errors.map((e) => (
                  <div key={e.line} className="flex gap-3 border-b px-3 py-2 last:border-0">
                    <span className="w-16 shrink-0 text-muted-foreground">Linha {e.line}</span>
                    <span className="w-48 shrink-0 tabular-nums">{e.value || '—'}</span>
                    <span className="text-destructive">{e.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{kind === 'tracker' ? 'Um rastreador por linha: IMEI;ESN' : 'Um chip por linha: ICCID;Linha;IMSI'}</Label>
              <Textarea value={text} rows={10} className="font-mono text-xs" onChange={(e) => setText(e.target.value)}
                placeholder={kind === 'tracker' ? '864893030000001;A1B2C3D4\n864893030000002' : '8955101234567890123;11999990000;724101234567890'} />
              <p className="text-xs text-muted-foreground">{rows.length} linha(s). Pode colar direto da planilha. Máximo de {MAX_LINES} por vez.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {kind === 'tracker' ? (
                <div className="space-y-1.5">
                  <Label>Modelo (para todos)</Label>
                  <SearchableSelect value={modelId === NONE ? null : modelId} onChange={(v) => setModelId(v ?? NONE)} noneLabel="Sem modelo"
                    options={(models?.items ?? []).map((m) => ({ value: m.id, label: m.name, hint: m.manufacturer_name }))} />
                </div>
              ) : (
                <div className="space-y-1.5">
                  {/* Plano obrigatório para chips (também validado no servidor) */}
                  <Label>Plano de dados (para todos) <span className="text-destructive">*</span></Label>
                  <div className="flex gap-2">
                    <SearchableSelect value={dataPlanId === NONE ? null : dataPlanId} onChange={(v) => setDataPlanId(v ?? NONE)}
                      options={(plans?.items ?? []).map((p) => ({ value: p.id, label: p.name, hint: p.compatible_carriers?.length ? p.compatible_carriers.map(dataPlanCarrierLabel).join(', ') : null }))} />
                    {/* Atalho: cadastrar plano sem sair da importação (somente com permissão) */}
                    {canCreate('stock_data_plans') && (
                      <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => setShowPlanForm(true)}
                        aria-label="Atalho: cadastrar novo plano de dados" title="Atalho: cadastrar novo plano de dados">
                        <Plus className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Situação (para todos)</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{manualStatusesFor(kind).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{report ? 'Fechar' : 'Cancelar'}</Button>
          {!report && (
            <Button onClick={run} disabled={rows.length === 0 || rows.length > MAX_LINES || importItems.isPending || (kind === 'chip' && dataPlanId === NONE)}>
              {importItems.isPending ? 'Importando...' : 'Importar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      {/* Plano criado pelo atalho já fica selecionado para a importação */}
      <StockDataPlanFormDialog open={showPlanForm} plan={null} onOpenChange={setShowPlanForm}
        onSuccess={(id) => setDataPlanId(id)} />
    </Dialog>
  );
}
